import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  Trash2,
  Copy,
  Check,
  Layers,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { ConfirmModal } from './ui/Modal';
import { useToast } from './ui/Toast';
import { envInitials } from '../utils/envAvatar';
import { requireWritableWorkspace } from '../plans/localGate';
import { requireApiCapacity } from '../plans/localApiUsage';
import LocalCapHint from './plans/LocalCapHint';

const Environment = ({
  environments,
  onEnvironmentsChange,
  selectedEnvironment,
  onSelectedEnvironmentChange,
  globalVariables: _globalVariables,
  workspaceVariables: _workspaceVariables,
  activeSelection,
  onSelectGlobal,
  onSelectWorkspace,
  onSelectEnvironment,
  onCreateEnvironment
}) => {
  const { t } = useTranslation();
  const [showDeleteEnvModal, setShowDeleteEnvModal] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);

  const { showToast } = useToast();

  const duplicateEnvironment = useCallback(
    async (env) => {
      // Plans v2: refused in a read-only cloud workspace, and past the cap
      // (counted across every own local workspace).
      if (!requireWritableWorkspace() || !(await requireApiCapacity('environments'))) return;
      // Names are how people tell environments apart, so a duplicate has to
      // arrive with one that is already distinct — "Staging copy", then
      // "Staging copy 2" — rather than a second identical row nobody can
      // identify until they rename it.
      const taken = new Set(environments.map((e) => e.name));
      let name = `${env.name} copy`;
      for (let n = 2; taken.has(name); n += 1) name = `${env.name} copy ${n}`;

      const copy = {
        ...env,
        // A fresh id, because everything downstream keys off it: reusing the
        // original's would make the copy indistinguishable from the source to
        // the selection, the persistence layer and the active-environment
        // pointer alike.
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        // Deep-copied so editing a variable in the copy cannot reach back into
        // the original — they would otherwise share the same row objects.
        variables: (env.variables || []).map((v) => ({ ...v })),
      };

      // Deliberately NOT activated. Duplicating is usually the first step of
      // "make a variant of this", and silently switching the active
      // environment would change what the next request sends.
      onEnvironmentsChange([...environments, copy]);
      showToast(t('environment.duplicatedSuccess', { name: copy.name }), 'success');
    },
    [environments, onEnvironmentsChange, showToast, t]
  );

  const deleteEnvironment = useCallback((id) => {
    const envToDelete = environments.find(env => env.id === id);
    setEnvironmentToDelete(envToDelete);
    setShowDeleteEnvModal(true);
  }, [environments]);
  const handleDeleteEnvironment = useCallback(() => {
    if (environmentToDelete) {
      onEnvironmentsChange(environments.filter(env => env.id !== environmentToDelete.id));
      if (selectedEnvironment?.id === environmentToDelete.id) {
        onSelectedEnvironmentChange(null);
      }
      showToast(t('environment.deletedSuccess', { name: environmentToDelete.name }), 'success');
      setEnvironmentToDelete(null);
      setShowDeleteEnvModal(false);
    }
  }, [environmentToDelete, environments, onEnvironmentsChange, selectedEnvironment, onSelectedEnvironmentChange, showToast, t]);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredEnvironments = searchTerm.trim()
    ? environments.filter((env) =>
        (env.name || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    : environments;

  const isGlobalSelected = activeSelection?.type === 'global';
  const isWorkspaceSelected = activeSelection?.type === 'workspace';

  return (
    <div className="env-side">
      <div className="env-side__search">
        <Search size={13} className="env-side__search-icon" aria-hidden />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('environment.searchPlaceholder', 'Search environments…')}
          className="env-side__search-input"
          autoComplete="off"
        />
        {searchTerm && (
          <button
            type="button"
            className="env-side__search-clear"
            onClick={() => setSearchTerm('')}
            aria-label={t('sidebar.clearSearch')}
          >
            <X size={12} />
          </button>
        )}
      </div>

      <div className="env-side__scroll">
        <div className="env-side__group">
          <p className="env-side__legend">{t('environment.globalsLegend')}</p>
          <button
            type="button"
            aria-current={isGlobalSelected ? 'true' : undefined}
            onClick={() => onSelectGlobal?.()}
            className={`env-side__row${isGlobalSelected ? ' env-side__row--active' : ''}`}
          >
            <Globe size={16} className="env-side__row-icon env-side__row-icon--global" aria-hidden />
            <span className="env-side__row-label">Global variables</span>
          </button>
          <button
            type="button"
            aria-current={isWorkspaceSelected ? 'true' : undefined}
            onClick={() => onSelectWorkspace?.()}
            className={`env-side__row${isWorkspaceSelected ? ' env-side__row--active' : ''}`}
            title={t('environment.workspaceVarsTitle')}
          >
            <Layers size={16} className="env-side__row-icon env-side__row-icon--workspace" aria-hidden />
            <span className="env-side__row-label">{t('environment.workspaceVariables')}</span>
          </button>
        </div>

        <div className="env-side__group">
          <p className="env-side__legend">{t('environment.environmentsLegend')}</p>
          {environments.length === 0 ? (
            <p className="env-side__empty">{t('environment.noEnvironmentsYet')}</p>
          ) : filteredEnvironments.length === 0 ? (
            <p className="env-side__empty">{t('sidebar.noResultsFound')}</p>
          ) : (
            filteredEnvironments.map((env) => {
              const isActive = selectedEnvironment?.id === env.id;
              const isEditing =
                activeSelection?.type === 'environment' && activeSelection?.envId === env.id;
              return (
                <div
                  key={env.id}
                  role="button"
                  tabIndex={0}
                  aria-current={isEditing ? 'true' : undefined}
                  onClick={() => onSelectEnvironment?.(env)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectEnvironment?.(env);
                    }
                  }}
                  className={`env-side__row env-side__row--env group${isEditing ? ' env-side__row--active' : ''}`}
                >
                  <span className="env-side__avatar">
                    {envInitials(env.name)}
                  </span>
                  <span className="env-side__row-label">{env.name}</span>
                  <span className="env-side__trailing">
                    {isActive && (
                      <span className="env-side__active-dot" title={t('environment.useEnvironment')} aria-hidden />
                    )}
                    <span className="env-side__actions" onClick={(e) => e.stopPropagation()}>
                      {!isActive && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectedEnvironmentChange(env);
                            showToast(t('environment.activatedEnvironment', { name: env.name }), 'success');
                          }}
                          className="env-side__action-btn"
                          title={t('environment.useEnvironment')}
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateEnvironment(env);
                        }}
                        className="env-side__action-btn"
                        title={t('environment.duplicateEnvironmentBtn')}
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteEnvironment(env.id);
                        }}
                        className="env-side__action-btn env-side__action-btn--danger"
                        title={t('environment.deleteEnvironmentBtn')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </span>
                </div>
              );
            })
          )}

          <button
            type="button"
            onClick={() => onCreateEnvironment?.()}
            className="env-side__new"
            title={t('environment.newEnvironmentBtn')}
          >
            <Plus size={15} />
            <span>{t('environment.newEnvironmentBtn')}</span>
          </button>
          {/* Plans v2 card 3.3: "1 / 1" once the cap is reached; the button
              above still opens the upgrade. */}
          <LocalCapHint kind="environments" style={{ alignSelf: 'flex-start', margin: '4px 8px' }} />
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteEnvModal}
        onClose={() => {
          setShowDeleteEnvModal(false);
          setEnvironmentToDelete(null);
        }}
        onConfirm={handleDeleteEnvironment}
        title={t('environment.deleteTitle')}
        message={t('environment.deleteMessage', { name: environmentToDelete?.name ?? '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </div>
  );
};

export default Environment;
